import React, { useEffect, useCallback, useState, useRef } from 'react'
import Spinner from '../../../components/Spinner'
import { useDispatch, useSelector } from "react-redux";
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { Fade } from 'react-reveal'
import { selectUserInfo } from "../../../features/users/userInfoSlice";
import { closeMessageWebsite, createWebsite, deleteWebsite, fetchWebsites, selectAll, updateWebsite } from '../../../features/websites/websitesSlice'
import DashedButton from '../../../components/elements/Buttons/DashedButton'
import FormDomain from '../../../components/elements/forms/FormDomain'
import { getConcurrents, removeConcurrentByIdParentWebsite } from '../../../features/concurrents/concurrentsSlice';
import { ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';
import DefaultBtn from '../../../components/elements/Buttons/DefaultBtn';
import SuccessDialog from '../../../components/SuccessDialog';
import SearchInput from '../../../components/elements/Inputs/SearchInput';
import { useSubscriptionGard } from '../../../guards/subscription/useSubscriptionGard';
import ToastAction from '../../../components/ToastAction';
import { classNames, getPluralWord, ToradorFilterDom } from '../../../utils';
import { selectorTour } from '../../../features/tour/tourSlice';


const AddWebsites = () => {
	const history = useHistory()
	const listRef = useRef()
	const dispatch = useDispatch();
	const [addNew, setAddNew] = useState(false)
	const showMessage = useSelector(state => state.websites.showMessage)
	const userInfoContext = useSelector(selectUserInfo)
	const websites = useSelector(selectAll)
	const errorWebsites = useSelector(state => state.websites.error)
	const concurrents = useSelector(getConcurrents)
	const loadingWebsite = useSelector(state => state.websites.loading)
	const [resultSearch, setResultSearch] = useState([])
	const isTourLaunched = useSelector(selectorTour)

	const { websites: websitesGuard } = useSubscriptionGard()

	useEffect(() => {
		if (websites.length > 0) return
		dispatch(fetchWebsites({ company_id: userInfoContext?.user?.company_id }))
	}, [userInfoContext?.user?.company_id])



	useEffect(() => {
		if (!loadingWebsite && !errorWebsites.status) setAddNew(false)
	}, [loadingWebsite, errorWebsites])

	const _refreshWebsite = useCallback(() => {
		dispatch(fetchWebsites({ company_id: userInfoContext?.user?.company_id }))
	}, [dispatch, userInfoContext?.user?.company_id])

	const _saveSite = useCallback((site) => {
		if (site.website_id) dispatch(updateWebsite({ site }))
		else {
			if (!websitesGuard.canAdd) return
			if (!!websites.find(website => website.site_url?.trim() === site.site_url?.trim())) {
				return toast.custom((t) => <ToastAction saraExpression="wink" toastRef={t} label="Ce site existe déjà !" description="Vous ne pouvez pas enregistrer deux sites avec la même URL." />
				)
			}
			dispatch(createWebsite({ company_id: userInfoContext?.user?.company_id, site }))
		}
	}, [userInfoContext?.user?.company_id, websites, websitesGuard.canAdd])

	const _deleteSite = useCallback((id) => {
		if (id) {
			dispatch(deleteWebsite({ company_id: userInfoContext?.user?.company_id, id }))
			if (concurrents?.length > 0) {
				const concurrentsToRemove = concurrents.filter(concurrent => concurrent.website_parent_id === id).map(concurrent => concurrent.id)
				dispatch(removeConcurrentByIdParentWebsite(concurrentsToRemove))
			}
		}
		else setAddNew(false)
	}, [userInfoContext?.user?.company_id, concurrents])

	const _addWebsite = useCallback(() => {
		if (!websitesGuard.canAdd) {
			const label = websitesGuard.limit
				? `Votre forfait actuel ne vous permet pas de rajouter plus de ${websitesGuard.limit} ${getPluralWord('site', websitesGuard.limit)}.`
				: "Vous n'avez pas encore souscrit à un forfait."
			const description = websitesGuard.limit ? "Voulez-vous changer de forfait ?" : "Voulez-vous souscrire maintenant ?"
			toast.custom((t) => <ToastAction saraExpression="wink" toastRef={t} label={label} description={description} okAction={() => history.push('/account/forfait')} />
			)
			return
		}
		flushSync(() => {
			setAddNew(true)
		})
		websites.length >= 4 && listRef.current?.lastElementChild?.scrollIntoView()
	}, [websitesGuard.limit, listRef.current, websites])

	const _remove = useCallback(() => {
		setAddNew(false)
	}, [])

	const _closeMessageDialog = useCallback(() => {
		dispatch(closeMessageWebsite())
	}, [])

	const _searchWebsites = useCallback(e => {
		const resultSearch = ToradorFilterDom(e.target.value, websites)
		setResultSearch(resultSearch)
	}, [websites])

	if (loadingWebsite && !addNew && websites.length <= 0)
		return <div className="w-full mx-auto flex justify-center mt-40 second-step"><Spinner /></div>

	return (
		<div className="max-w-4xl mx-auto space-y-3 p-2 second-step">
			<SuccessDialog open={showMessage} close={_closeMessageDialog} description="Votre site a été crée avec succès" display={true} />
			<div className="flex mt-10 justify-center">
				<div className="w-full max-w-lg lg:max-w-md space-y-5 mx-auto">
					<div className="flex items-center space-x-2">
						<SearchInput onChange={_searchWebsites} id='search' name='search' placeholder='Rechercher...' />
						<button type='button' className="shadow px-2 py-2 rounded-2xl bg-white items-center space-x-1 text-gray-500 hidden">
							<FunnelIcon className="h-6" />
							<span>Filtrer</span>
						</button>
					</div>
					<p className="text-gray-400 text-xs text-center font-semibold">Merci de remplir l'url de votre ou de vos sites internet</p>
				</div>
			</div>
			{websites.length > 0 && <div className="flex items-center justify-end">
				<DefaultBtn Icon={ArrowPathIcon} label={loadingWebsite ? 'Actualisation...' : 'Actualiser'} classIcon={loadingWebsite ? 'animate-spin' : ''} disabled={loadingWebsite} onClick={_refreshWebsite} />
			</div>}

			<section
				ref={listRef}
				className={
					classNames("max-h-[350px] overflow-y-auto rounded-2xl p-4 scroll-smooth space-y-2 overflow-x-hidden",
						websites.length >= 4 ? "bg-gray-100 shadow-inner" : "")}
			>
				{resultSearch.length > 0 && resultSearch.map((site, _index) =>
					<Fade top key={site.id}>
						<FormDomain
							key={site.id}
							site={site}
							company_id={userInfoContext?.user?.company_id}
							saveSite={_saveSite}
							deleteSite={_deleteSite} />
					</Fade>
				)}
				{
					resultSearch.length <= 0 && websites.length > 0 && websites.map((site, _index) =>
						<Fade top key={site.id}>
							<FormDomain
								key={site.id}
								site={site}
								company_id={userInfoContext?.user?.company_id}
								saveSite={_saveSite}
								deleteSite={_deleteSite} />
						</Fade>
					)
				}

				{
					addNew && <Fade>
						<FormDomain
							site={{}}
							canAdd={websitesGuard.canAdd}
							company_id={userInfoContext?.user?.company_id}
							saveSite={_saveSite}
							deleteSite={_remove} />
					</Fade>
				}
			</section>
			<DashedButton
				disabled={isTourLaunched || loadingWebsite || addNew}
				onClick={_addWebsite} label="Ajouter un site"
			/>
		</div>
	)
}

export default AddWebsites